import React, { useEffect, useState } from 'react';
import { fetchCurrentRoles } from '../lib/currentRolesApi';
import { isSupabaseConfigured } from '../lib/supabaseClient';
import type { CurrentRoleCard } from '../types';

const Education: React.FC = () => {
    const [educationData, setEducationData] = useState<CurrentRoleCard[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hoveredImage, setHoveredImage] = useState<string | null>(null);
    const [cursor, setCursor] = useState({ x: 0, y: 0 });
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedMobileRoleId, setSelectedMobileRoleId] = useState<string | null>(null);

    const statusLabel: Record<CurrentRoleCard['status'], string> = {
        active: 'Active',
        remote: 'Active (remote)',
        handed_over: 'Handed over',
        left: 'Left',
    };

    const statusDotClass: Record<CurrentRoleCard['status'], string> = {
        active: 'bg-green-400',
        remote: 'bg-green-400',
        handed_over: 'bg-yellow-400',
        left: 'bg-gray-400',
    };

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!isSupabaseConfigured()) {
                setError(
                    'Supabase neni nakonfigurovan. Zkopiruj .env.example na .env.local a dopln REACT_APP_SUPABASE_URL a REACT_APP_SUPABASE_PUBLISHABLE_KEY.'
                );
                setLoading(false);
                return;
            }

            try {
                const data = await fetchCurrentRoles();
                if (!cancelled) {
                    setEducationData(data);
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : 'Nepodarilo se nacist sekci Current.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const visibleRoles = isExpanded ? educationData : educationData.slice(0, 3);
    const selectedMobileRole = educationData.find((item) => item.id === selectedMobileRoleId) ?? null;

    const handleRoleClick = (item: CurrentRoleCard) => {
        setSelectedMobileRoleId((prev) => (prev === item.id ? null : item.id));
    };

    return (
        <section id="education" className="py-12 md:py-20 bg-surface">
            <div className="max-w-6xl mx-auto px-4">
                <h2 className="text-4xl md:text-6xl font-bold mb-8 md:mb-16">CURRENT ROLES</h2>

                {loading && (
                    <p className="brutalist-text text-sm md:text-base mb-6" aria-live="polite">
                        Loading current roles...
                    </p>
                )}

                {error && !loading && (
                    <p className="brutalist-text text-sm md:text-base text-red-300 border-2 border-red-500 p-4 bg-red-950/40 mb-6" role="alert">
                        {error}
                    </p>
                )}

                {!loading && !error && educationData.length === 0 && (
                    <p className="brutalist-text text-sm md:text-base mb-6">No current roles to display.</p>
                )}

                {!loading && !error && educationData.length > 0 && (
                <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute w-full h-1 bg-surface-alt top-8 hidden md:block"></div>
                    
                    {/* Timeline items */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {visibleRoles.map((item) => (
                            <div 
                                key={item.id}
                                className="brutalist-container p-6 relative"
                                onMouseEnter={() => setHoveredImage(item.image)}
                                onMouseMove={(e) => setCursor({ x: e.clientX, y: e.clientY })}
                                onMouseLeave={() => setHoveredImage(null)}
                                onClick={() => handleRoleClick(item)}
                            >
                                <div className="md:absolute md:-top-3 md:left-1/2 md:transform md:-translate-x-1/2 w-4 h-4 bg-accent rounded-full mb-4 md:mb-0"></div>
                                <h3 className="text-xl font-bold mb-2">{item.school}</h3>
                                <p className="font-medium mb-2">{item.degree}</p>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${statusDotClass[item.status]}`}></span>
                                    <span className="text-xs uppercase tracking-wide text-gray-200">
                                        {statusLabel[item.status]}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-300">{item.description}</p>
                            </div>
                        ))}
                    </div>
                    {educationData.length > 3 && (
                        <div className="mt-8 text-center">
                            <button
                                type="button"
                                className="brutalist-button"
                                onClick={() => setIsExpanded((prev) => !prev)}
                            >
                                {isExpanded ? 'SHOW LESS' : 'SHOW MORE'}
                            </button>
                        </div>
                    )}
                </div>
                )}
            </div>
            {hoveredImage && (
                <div
                    className="pointer-events-none fixed z-50 hidden md:block"
                    style={{ left: cursor.x, top: cursor.y, transform: 'translate(-50%, -50%)' }}
                    aria-hidden="true"
                >
                    <img
                        src={hoveredImage}
                        alt=""
                        className="w-[30rem] h-[30rem] object-cover opacity-90"
                    />
                </div>
            )}
            {selectedMobileRole && (
                <div className="fixed inset-0 z-50 bg-background/85 p-4 md:hidden">
                    <div className="relative mx-auto mt-8 max-w-sm">
                        <button
                            type="button"
                            className="absolute -top-3 -right-3 h-10 w-10 rounded-full border-2 border-text bg-background text-2xl leading-none"
                            onClick={() => setSelectedMobileRoleId(null)}
                            aria-label="Close image preview"
                        >
                            ×
                        </button>
                        <img
                            src={selectedMobileRole.image}
                            alt={selectedMobileRole.school}
                            className="w-full h-auto border-3 border-text object-cover"
                            onClick={() => setSelectedMobileRoleId(null)}
                        />
                    </div>
                </div>
            )}
        </section>
    );
};

export default Education;